import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";

import Dialog, { DialogContent, DialogActions } from "components/common/Dialog";
import Button from "components/common/Button";
import SearchableAutoComplete from "components/common/AutoComplete";
import WeeklyShiftSelector from "components/CheckInCheckOut/WeeklyShiftSelector";
import ShiftSelectionSuccessModal from "components/CheckInCheckOut/ShiftSelectionSuccessModal";
import { AlertType } from "components/common/Snackbar";
import { setEmployeeSchedule } from "services/CheckInCheckOut/CheckInAndBreaks";
import { showSnackbar } from "store/actions/Utility";
import {
  INITIAL_WEEK_SCHEDULE,
  prepareScheduleDataForAPI,
  isScheduleValid,
  getTodaySchedule,
  TIMEZONE_DROPDOWN_OPTIONS,
  getDefaultTimezoneForCountry,
  DAYS_ORDER,
} from "components/Screens/Timesheet/helpers";

import "components/CheckInCheckOut/SelectShiftModal/SelectShiftModal.scss";

function SelectShiftModal({ open, onClose, onConfirm, timeoffCountry }) {
  const reduxDispatch = useDispatch();

  const defaultTimezone = getDefaultTimezoneForCountry(timeoffCountry);

  const [weekSchedule, setWeekSchedule] = useState(INITIAL_WEEK_SCHEDULE);
  const [sameForAllDays, setSameForAllDays] = useState(false);
  const [timezone, setTimezone] = useState(defaultTimezone);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [clockEntryErrorCode, setClockEntryErrorCode] = useState(undefined);
  const [todayScheduleForModal, setTodayScheduleForModal] = useState(null);

  const handleConfirm = async () => {
    if (!isScheduleValid(weekSchedule)) {
      reduxDispatch(
        showSnackbar({
          type: AlertType.error,
          message: "Please set shift times for at least one working day",
          dispatch: reduxDispatch,
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
      const employeeId = loggedInUser?.userId;

      if (!employeeId) {
        reduxDispatch(
          showSnackbar({ type: AlertType.error, message: "Employee ID not found", dispatch: reduxDispatch })
        );
        setIsSubmitting(false);
        return;
      }

      const scheduleData = prepareScheduleDataForAPI(weekSchedule, timezone);
      await setEmployeeSchedule(employeeId, scheduleData);

      const todaySchedule = getTodaySchedule(weekSchedule);
      setTodayScheduleForModal(todaySchedule);

      setIsClockedIn(false);
      setClockInTime(null);
      setShiftData({
        weekSchedule,
        clockedIn: false,
        errorCode: undefined,
      });

      setShowSuccessModal(true);
    } catch (error) {
      reduxDispatch(showSnackbar({ type: AlertType.error, message: error.message, dispatch: reduxDispatch }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setWeekSchedule(INITIAL_WEEK_SCHEDULE);
    setSameForAllDays(false);
    setTimezone(defaultTimezone);
    setClockEntryErrorCode(undefined);
    setTodayScheduleForModal(null);
    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);

    if (shiftData) {
      onConfirm(shiftData);
      setShiftData(null);
    }

    handleClose();
  };

  return (
    <>
      <Dialog
        open={open && !showSuccessModal}
        onClose={handleClose}
        width="688px"
        title={
          <div className="select-shift-modal__title-section">
            <div className="select-shift-modal__icon">
              <ScheduleOutlinedIcon />
            </div>
            <div className="select-shift-modal__title-content">
              <h2 className="heading-h2-semibold">Confirm your shift timings</h2>
            </div>
            <p className="select-shift-modal__description para-body-m-regular">
              Before we continue, please confirm the shift timings you've agreed on with your client. Don't worry —
              you'll only need to do this once!
            </p>
          </div>
        }
        content={
          <DialogContent>
            <div className="select-shift-modal__content">
              <div className="select-shift-modal__timezone">
                <SearchableAutoComplete
                  label="Time zone"
                  name="timezone"
                  placeholder="Select timezone"
                  options={TIMEZONE_DROPDOWN_OPTIONS}
                  value={timezone}
                  onChange={(value) => setTimezone(value || defaultTimezone)}
                />
              </div>
              <WeeklyShiftSelector
                weekSchedule={weekSchedule}
                onWeekScheduleChange={setWeekSchedule}
                sameForAllDays={sameForAllDays}
                onSameForAllDaysChange={setSameForAllDays}
                helperText="Please enter all times in your local timezone."
                showHelperText
              />
            </div>
          </DialogContent>
        }
        actions={
          <DialogActions
            action1={<Button label="Cancel" size="large" variant="text" onClick={handleClose} />}
            action2={
              <Button
                label="Confirm Shift Time"
                size="large"
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                isDisabled={!isScheduleValid(weekSchedule) || isSubmitting}
              />
            }
          />
        }
        size="sm"
      />

      <ShiftSelectionSuccessModal
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        shiftDay={
          todayScheduleForModal
            ? DAYS_ORDER.find((day) => day.dayOfWeek === new Date().getDay())?.fullName || null
            : null
        }
        shiftStartTime={todayScheduleForModal?.startTime || null}
        shiftEndTime={todayScheduleForModal?.endTime || null}
        clockInTime={clockInTime}
        isClockedIn={isClockedIn}
        errorCode={clockEntryErrorCode}
        timezone={timezone}
      />
    </>
  );
}

SelectShiftModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  timeoffCountry: PropTypes.string,
};

SelectShiftModal.defaultProps = {
  timeoffCountry: null,
};

export default SelectShiftModal;
